import { useMemo } from 'react';
import { useLocationGroupStore } from '@frontend/scope';
import type { FeatureDecision } from '@frontend/scope-policy';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import type { TopBarSurfaceId, GlobalPanelSurfaceId, EntryPointSurfaceId, BannerSurfaceId } from './workspace/types';
import {
  resolveWorkspacePathDecision,
  resolveWorkspaceTopBarDecision,
  resolveWorkspacePanelDecision,
  resolveWorkspaceEntryPointDecision,
  isPopupNotificationAllowed,
  isAllowedInNotificationCenter,
  isBannerAllowedInScope,
  AVAILABLE_DECISION,
} from './workspace/decision-gate';

// ─────────────────────────────────────────────────────────────────────────────
// useScopePolicy — scope-dispatching access hook
// ─────────────────────────────────────────────────────────────────────────────
//
// This hook reads the current scope and dispatches to the correct scope folder's
// resolution functions. Each scope folder owns its own surface→capability mapping
// and resolution logic.
//
// Currently only 'workspace' has restrictions. All other scopes are unrestricted.
//
// ## Adding a new scope:
//   1. Create src/<scope-name>/decision-gate.ts with the same resolver shape
//   2. Import the resolvers here
//   3. Add a case branch in each switch statement below
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The single hook all UI components use to check scope-based access.
 *
 * Dispatches to scope-specific resolvers based on selectedScope.scopeType:
 * - 'workspace' → workspace/decision-gate.ts (deny-by-default, allowlist-based)
 * - undefined/null → AVAILABLE (fail-open during initialization)
 * - all others → AVAILABLE (unrestricted)
 */
export const useScopePolicy = () => {
  // Change this logic, once we start supporting other scope types, to dispatch to the correct scope's decision-gate.ts
  const isWorkspaceScope = useLocationGroupStore((state) => state.selectedScope?.scopeType === 'workspace');

  // Read the full selectedScope on-demand (no subscription). Decision functions
  // call this at invocation time, so they always get the latest scope.
  const getScope = () => useLocationGroupStore.getState().selectedScope;

  return useMemo(() => {
    // ─── Path resolution ──────────────────────────────────────────────────────

    /**
     * Resolves a URL pathname to a FeatureDecision for the current scope.
     * Dispatches to the active scope's path resolver.
     */
    const getDecisionForPath = (path: string): FeatureDecision => {
      const scope = getScope();
      if (!scope) return AVAILABLE_DECISION;

      switch (scope.scopeType) {
        case 'workspace':
          return resolveWorkspacePathDecision(scope, path);
        // future: case 'division': return resolveDivisionPathDecision(scope, path);
        default:
          return AVAILABLE_DECISION;
      }
    };

    // ─── Top-bar resolution ───────────────────────────────────────────────────

    /**
     * Resolves a top-bar surface to a FeatureDecision for the current scope.
     */
    const getDecisionForTopBarSurface = (surfaceId: TopBarSurfaceId): FeatureDecision => {
      const scope = getScope();
      if (!scope) return AVAILABLE_DECISION;

      switch (scope.scopeType) {
        case 'workspace':
          return resolveWorkspaceTopBarDecision(scope, surfaceId);
        default:
          return AVAILABLE_DECISION;
      }
    };

    // ─── Panel resolution ─────────────────────────────────────────────────────

    /**
     * Resolves a global panel surface to a FeatureDecision for the current scope.
     */
    const getDecisionForGlobalPanelSurface = (surfaceId: GlobalPanelSurfaceId): FeatureDecision => {
      const scope = getScope();
      if (!scope) return AVAILABLE_DECISION;

      switch (scope.scopeType) {
        case 'workspace':
          return resolveWorkspacePanelDecision(scope, surfaceId);
        default:
          return AVAILABLE_DECISION;
      }
    };

    // ─── Entry-point resolution ───────────────────────────────────────────────

    /**
     * Resolves an entry-point surface to a FeatureDecision for the current scope.
     */
    const getDecisionForEntryPointSurface = (surfaceId: EntryPointSurfaceId): FeatureDecision => {
      const scope = getScope();
      if (!scope) return AVAILABLE_DECISION;

      switch (scope.scopeType) {
        case 'workspace':
          return resolveWorkspaceEntryPointDecision(scope, surfaceId);
        default:
          return AVAILABLE_DECISION;
      }
    };

    return {
      /** Boolean: true if the user can navigate to this path in the current scope. */
      canAccessPath: (path: string): boolean => getDecisionForPath(path).availability === 'available',

      /** Full decision for a path — use when you need the explanation or tooltip. */
      getDecisionForPath,

      /** Boolean: true if a top-bar surface (button or panel) is accessible. */
      canAccessTopBarSurface: (surfaceId: TopBarSurfaceId): boolean =>
        getDecisionForTopBarSurface(surfaceId).availability === 'available',

      /** Full decision for a top-bar surface. */
      getDecisionForTopBarSurface,

      /** Boolean: true if a slide-in global panel is accessible. */
      canAccessGlobalPanelSurface: (surfaceId: GlobalPanelSurfaceId): boolean =>
        getDecisionForGlobalPanelSurface(surfaceId).availability === 'available',

      /** Full decision for a global panel surface. */
      getDecisionForGlobalPanelSurface,

      /** Boolean: true if a dashboard tile, quick-action button, or shortlink is accessible. */
      canAccessEntryPointSurface: (surfaceId: EntryPointSurfaceId): boolean =>
        getDecisionForEntryPointSurface(surfaceId).availability === 'available',

      /** Full decision for an entry-point surface. */
      getDecisionForEntryPointSurface,

      /**
       * Boolean: true if a popup notification of this type should fire.
       *
       * Notifications follow their parent feature's availability:
       * - Parent available → popup shows
       * - Parent blocked/hidden → popup silently suppressed
       *
       * This is popup-only. For the persisted Notification Center list use
       * `canShowInNotificationCenter` instead.
       */
      canShowPopupNotification: (notificationType: string): boolean =>
        isPopupNotificationAllowed(getScope(), notificationType),

      /**
       * Boolean: true if a Notification Center (persisted list) item of this
       * Firestore type should be visible in the current scope.
       *
       * Same pattern as `canShowPopupNotification` — pass the Firestore
       * `NotificationType` value (e.g. `notification.type`). Checks against
       * `ALLOWED_IN_NOTIFICATION_CENTER` in registry.ts.
       *
       * - Non-workspace scope → always true (unrestricted)
       * - Workspace scope → true if type is in ALLOWED_IN_NOTIFICATION_CENTER
       */
      canShowInNotificationCenter: (notificationType: NotificationType): boolean =>
        isAllowedInNotificationCenter(getScope(), notificationType),

      /**
       * Boolean: true if a global banner with this surfaceId should be shown.
       *
       * - Non-workspace scope → always true (unrestricted)
       * - Workspace scope → true if the banner's parent capability is allowed
       * - No surfaceId → false in workspace (deny-by-default for untagged banners)
       *
       * Used by GlobalBannerDynamic to suppress banners for blocked features.
       */
      canShowBanner: (surfaceId?: BannerSurfaceId): boolean => isBannerAllowedInScope(getScope(), surfaceId),
    };
  }, [isWorkspaceScope]);
};
